/**
 * Mileage reimbursement, shared by the phone's live preview and the API.
 *
 * The preview under the miles field ("32.0 mi x $0.7150 = $22.88") must equal
 * the stored amount to the cent, so both sides call this exact function. See
 * money.ts for why that requires integer cents.
 */
import { mulRate, type Cents } from './money';

export interface MileageInput {
  /** One-way distance as entered. */
  miles: string | number;
  /** Rate per mile as a decimal string, e.g. "0.7150". Scale 4. */
  ratePerMile: string;
  roundTrip: boolean;
}

export interface MileageResult {
  /** Total billable miles -- doubled when round-trip. Stored on the row. */
  totalMiles: string;
  amount: Cents;
}

export function calculateMileage(input: MileageInput): MileageResult {
  const entered = typeof input.miles === 'number' ? input.miles : Number(input.miles);
  if (!Number.isFinite(entered) || entered <= 0) {
    throw new Error(`Miles must be a positive number, got ${JSON.stringify(input.miles)}`);
  }

  // Double BEFORE converting to money so the doubling is exact, and store the
  // doubled figure: the GL export shows the miles actually reimbursed, and
  // "32 mi" on a round trip that paid for 64 would be an audit question.
  const totalMiles = (Math.round(entered * 100) * (input.roundTrip ? 2 : 1)) / 100;
  const totalMilesStr = totalMiles.toFixed(2);

  return { totalMiles: totalMilesStr, amount: mulRate(totalMilesStr, input.ratePerMile, 4) };
}

/**
 * Pick the rate in force on a date from a set of effective-dated rows.
 * `effectiveTo` of null means open-ended. The DB also carries an exclusion
 * constraint preventing overlaps, so at most one row can match -- but this is
 * the client's copy of that decision and must agree at the boundary dates.
 */
export function rateEffectiveOn<T extends { effectiveFrom: string; effectiveTo: string | null }>(
  rates: readonly T[],
  date: string,
): T | undefined {
  return rates.find((r) => r.effectiveFrom <= date && (r.effectiveTo === null || date <= r.effectiveTo));
}

/**
 * Recover the one-way distance from what is stored.
 *
 * `mileage_entries.miles` holds the TOTAL billable distance, already doubled
 * for a round trip. An edit form shows the one-way figure the person typed, so
 * it has to undo that first -- otherwise editing a round trip twice doubles it
 * again each time, and the error compounds silently because every individual
 * save looks reasonable.
 */
export function oneWayMiles(storedTotalMiles: string, roundTrip: boolean): string {
  const total = Number(storedTotalMiles);
  if (!Number.isFinite(total)) throw new Error(`Not a distance: ${storedTotalMiles}`);
  return (roundTrip ? total / 2 : total).toFixed(2);
}
